import * as d3 from 'd3';
import { ScaleLinear } from 'd3';
import React, { useEffect, useMemo, useRef } from 'react';

import { TSPoint } from '../../HighLowTripleLineChart.types';
import styles from './LinePlot.module.scss';

export interface LinePlotProps {
  ts: Array<TSPoint<number, number>>;
  scaleX: ScaleLinear<number, number>;
  scaleY: ScaleLinear<number, number>;
  className?: string;
  selected?: TSPoint<number, number>;
  curve?: d3.CurveFactory;
}

const linePath =
  (
    scaleX: ScaleLinear<number, number>,
    scaleY: ScaleLinear<number, number>,
    curve: d3.CurveFactory
  ) =>
  (ts: Array<TSPoint<number, number>>) =>
    d3
      .line<typeof ts[0]>()
      .x(d => scaleX(d.index))
      .y(d => scaleY(d.value))
      .curve(curve)(ts);

export const LinePlot = ({
  ts,
  scaleX,
  scaleY,
  className,
  selected,
  curve = d3.curveLinear,
  ...rest
}: LinePlotProps) => {
  const pathRef = useRef<SVGPathElement>(null);

  const path = useMemo(
    () => linePath(scaleX, scaleY, curve)(ts),
    [ts, scaleX, scaleY]
  );

  useEffect(() => {
    const path = pathRef.current as SVGPathElement;
    d3.select(path).data([ts]);
  }, [ts]);

  return (
    <g className={className} {...rest}>
      <path
        ref={pathRef}
        className={styles.linePlotPath}
        data-line-plot-path
        data-testid="line-plot"
        vectorEffect="non-scaling-stroke"
        d={path ?? ''}
      />
      {selected && (
        <circle
          className={styles.circle}
          cx={scaleX(selected.index)}
          cy={scaleY(selected.value)}
          r="4"
          strokeDasharray="none"
          data-testid="selected-point"
        />
      )}
    </g>
  );
};
